import React, { useEffect, useMemo } from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useApp } from "../../design/AppContext";
import useNotification from "../../hook/useNotification";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { MdWarningAmber } from "react-icons/md";
import { EmptyState, RowButton, StatusChip } from "../../components/mui";

/**
 * 通知（設計稿 el-pm-template_4.html:2130-2165 #screen-notify）
 *
 * 舊系統每則通知前面是一個「知」「退」「請」的單字方塊，右邊一個紅點，
 * 標題寫「狀態變更」—— 看完還是不知道是誰、對哪一件、做了什麼。
 * 這一頁把每一則寫成一句完整的話，方塊圖示整個拿掉（design/ui.js 的 NOTIF_ICON
 * 與 NOTIFC 在這一頁刻意不使用，它們還留給右側抽屜的舊版面）。
 *
 * 版面規則：
 *   · 不自己包頁面內距，Layout 的內容區已經給了。
 *   · 整頁沒有 contained 主要按鈕（硬規則 1）——「全部標成已讀」是次要動作，
 *     使用者來這一頁是為了看內容，不是為了按那顆鈕。
 *   · 未讀用 RowButton tone="attention"（左側色條）＋一顆「還沒看」徽章，不是一個紅點。
 *
 * 資料沿用 hook/useNotification：清單、未讀數、標記已讀、SignalR 推播都在那裡，
 * 而且是模組層共用的同一份 —— 在這一頁按「全部標成已讀」，頁首的「通知 N」
 * 與右側抽屜會同時跟著變。
 */

/** 依序取第一個有值的欄位（後端這幾支的命名 snake / camel / Pascal 都出現過） */
function pick(o, ...keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const v = _.get(o, keys[i]);
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

/**
 * 時間寫法照設計稿：今天 09:12 / 昨天 18:00 / 9 月 18 日 11:30。
 *
 * 不用 design/domain.js 的 fmtRelative（「3 小時前」）—— 相對時間在通知清單裡
 * 會讓人算不出「那到底是上班前還是下班後的事」，跨日之後更是一片「2 天前」。
 */
function fmtNotifyTime(v) {
  if (!v) return "";
  const d = dayjs(v);
  if (!d.isValid()) return "";
  const now = dayjs();
  if (d.isSame(now, "day")) return `今日 ${d.format("HH:mm")}`;
  if (d.isSame(now.subtract(1, "day"), "day")) return `昨日 ${d.format("HH:mm")}`;
  if (d.isSame(now, "year")) return d.format("M 月 D 日 HH:mm");
  return d.format("YYYY 年 M 月 D 日 HH:mm");
}

/**
 * 一則通知的完整句子。
 *
 * 後端有兩種形狀：落地通知（帶 title）與 ActivityLog 推播（只有 description，
 * 像「建立任務「收銀主畫面…」」，前面少了是誰做的）。後者才補上人名 ——
 * title 本身通常已經是一句話，前面再黏一個人名會變成「黃子銘你有新的指派」。
 *
 * 兩個都沒有就回空字串，整列不畫（硬規則 10：沒有資料就不要假裝有，
 * 寧可少一列，也不要放一列寫著「通知」的空殼）。
 */
function sentenceOf(n) {
  const title = pick(n, "title", "Title");
  const body = pick(n, "description", "body", "content", "Description");
  const who = pick(n, "create_name", "creator_name", "user_name", "createName", "create_id", "CreateId");
  if (title) return body && body !== title ? `${title}：${body}` : String(title);
  if (body) return who ? `${who}${body}` : String(body);
  return "";
}

/**
 * 點下去要去哪。
 *
 * 後端給了 link 就用它的；沒給就自己組「專案詳情＋工項」這條全站共用的網址
 * （同 CommandPalette），兩邊才不會各走各的。
 * 連專案都認不出來時回空字串，該列就不畫 › 也不導頁 —— 點了沒反應比沒得點更糟。
 */
function targetOf(link, projectId, taskId) {
  if (link) return String(link);
  if (projectId == null) return "";
  return taskId != null ? `/Project/Detail/${projectId}?task=${taskId}` : `/Project/Detail/${projectId}`;
}

/** 清單端點有時回純陣列、有時回 { data_list, total_count } */
const toArray = (v) => {
  if (Array.isArray(v)) return v;
  if (Array.isArray(_.get(v, "data_list"))) return v.data_list;
  if (Array.isArray(_.get(v, "list"))) return v.list;
  if (Array.isArray(_.get(v, "items"))) return v.items;
  return [];
};

function NotifyIndex(props) {
  const navigate = useNavigate();
  const { toast } = useApp();
  const { GET_ProjectList } = props;
  const { notifications, unread, hasMore, loadingMore, loadMore, markAllRead, markRead } =
    useNotification();

  /*
   * 專案名字只為了每一列第二欄那一格。通知本身只帶 project_id，
   * 拿不到名字時那一格就整個不畫，不要顯示「專案 #12」這種內部編號。
   */
  useEffect(() => {
    GET_ProjectList({ Page: 0, PageCount: 0 }, null, null);
  }, [GET_ProjectList]);

  const projectName = useMemo(() => {
    const map = {};
    toArray(props.projects).forEach((p) => {
      if (p && p.id != null) map[String(p.id)] = p.name;
    });
    return map;
  }, [props.projects]);

  /* 沒有句子可寫的那幾則直接濾掉（理由見 sentenceOf） */
  const rows = useMemo(
    () =>
      (notifications || [])
        .map((n, i) => {
          const projectId = pick(n, "project_id", "projectId", "ProjectId");
          const taskId = pick(n, "task_id", "taskId", "TaskId");
          const link = pick(n, "link", "url", "Link");
          return {
            key: n.id != null ? `n-${n.id}` : `n-${i}`,
            id: n.id,
            text: sentenceOf(n),
            time: fmtNotifyTime(pick(n, "create_at", "createAt", "CreateAt")),
            project: projectId != null ? projectName[String(projectId)] : undefined,
            unread: !pick(n, "is_read", "isRead", "IsRead"),
            to: targetOf(link, projectId, taskId),
          };
        })
        .filter((r) => r.text),
    [notifications, projectName]
  );

  const openRow = (r) => {
    if (r.unread && r.id != null) markRead([r.id]);
    if (r.to) navigate(r.to);
  };

  return (
    <>
      <Stack direction="row" spacing={1.5} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="h1" component="h1">
          通知
        </Typography>
        <Box sx={{ flex: 1 }} />
        {/* 全部都看過了就不畫這顆鈕（硬規則 2）：按下去什麼都不會發生的按鈕，
            只會讓人懷疑自己是不是按錯了 */}
        {unread > 0 ? (
          <Button
            variant="outlined"
            type="button"
            onClick={() => {
              markAllRead();
              toast("已將所有通知標示為已讀");
            }}
          >
            全部標示為已讀
          </Button>
        ) : null}
      </Stack>

      {rows.length === 0 ? (
        <EmptyState
          sx={{ mt: "var(--space-6)" }}
          title="目前沒有通知"
          text={
            <>
              有工項指派給您、有人在您的工項中留言，或您負責的項目即將到期時，
              通知將顯示於此，右上角的「通知」也會同步顯示數量。
            </>
          }
        />
      ) : (
        <Card sx={{ mt: "var(--space-6)" }}>
          <List disablePadding>
            {rows.map((r) => (
              /* 整列是按鈕：可點範圍就是整列（遠大於 44px）而且帶文字，
                 不是一顆看不出是什麼的圖示（硬規則 3） */
              <RowButton
                key={r.key}
                type="button"
                tone={r.unread ? "attention" : ""}
                onClick={() => openRow(r)}
                chevron={!!r.to}
                title={r.text}
                meta={
                  r.time || r.project ? (
                    <>
                      {r.time ? <span>{r.time}</span> : null}
                      {r.project ? <span>{r.project}</span> : null}
                    </>
                  ) : null
                }
                side={
                  r.unread ? (
                    <StatusChip kind="attention" mark={<MdWarningAmber />}>
                      未讀
                    </StatusChip>
                  ) : null
                }
              />
            ))}
          </List>
        </Card>
      )}

      {/* 還有更舊的才畫這顆（硬規則 2）。它是次要動作，整頁維持零顆 primary */}
      {hasMore && rows.length > 0 ? (
        <Box sx={{ mt: "var(--space-4)" }}>
          <Button
            variant="outlined"
            type="button"
            onClick={loadMore}
            aria-disabled={loadingMore}
            loading={loadingMore}
            loadingPosition="start"
          >
            {loadingMore ? "載入中…" : "顯示更早的通知"}
          </Button>
        </Box>
      ) : null}
    </>
  );
}

NotifyIndex.propTypes = {
  projects: PropTypes.any,
  GET_ProjectList: PropTypes.func,
};

NotifyIndex.defaultProps = {
  projects: [],
  GET_ProjectList: () => {},
};

const mapStateToProps = (state) => ({
  projects: _.get(state, "Project.List", []),
});

const mapDispatchToProps = (dispatch) => ({
  GET_ProjectList: (payload, callback, loading) =>
    dispatch({ type: "GET_ProjectList", payload, callback, loading }),
});

export default connect(mapStateToProps, mapDispatchToProps)(NotifyIndex);
